import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { MyrAPIService } from '../../../api/myr/myrAPI';
import { IReduxRootState } from '../../../app/reduxRootState.interface';
import { ErrorResolution } from '../../../app/common/errorResolution/errorResolution';
import { ErrorResolutionActions } from '../../../app/common/errorResolution/errorResolutionActions';
import { NotificationsActions } from '../../../app/common/notifications/notificationsActions';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import AccountSecurityActions from '../accountSecurityActions';
import { UpdateUsernameAction, UpdateUsernameActionType } from './updateUsernameActionType';
import { IUpdateUsernameRequest } from './updateUsernameRequest';
import { MyrAPIEndpoints } from '../../../api/myr/myrAPIEndpoints.constant';
import { RevokeSessionActionTypes } from '../revokeSession/revokeSessionActionTypes';
import ErrorRedirectService from '../../../routes/error/errorRedirect.service';
import { NOTIFICATIONS } from '../../../app/common/constants/styles';
import { NotificationType } from '../../../app/common/notifications/notificationType.enum';
import { RevokeSessionService } from '../revokeSession/revokeSession.services';

export class UpdateUsernameActions {
    public static updateMemberUsername(
        data: IUpdateUsernameRequest,
        signOutFromAllDevices: boolean,
    ): ThunkAction<void, IReduxRootState, {}, AnyAction> {
        return async (
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            getState: () => IReduxRootState,
        ) => {
            try {
                dispatch(request());
                const response: any = await MyrAPIService.sendRequest(
                    MyrAPIEndpoints.USERNAME,
                    'PUT',
                    data,
                );
                if (signOutFromAllDevices) {
                    await handleSessionRevocation(dispatch, getState, response);
                } else {
                    success(dispatch, response.username);
                }
            } catch (error) {
                const errorResolution = error as ErrorResolution;
                failure(dispatch, errorResolution);
            }
        };

        async function handleSessionRevocation(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            getState: () => IReduxRootState,
            response: any,
        ) {
            try {
                dispatch({ type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_REQUEST });
                const sessionRevokeRespone = await RevokeSessionService.requestSessionRevoke();
                if (!!sessionRevokeRespone) {
                    const tokenExpirationUrl = getState().app.clientConfig.tokenExpirationUrl;
                    successWithSessionRevoke(dispatch, response.username, tokenExpirationUrl);
                }
            } catch (error) {
                const errorResolution = error as ErrorResolution;
                success(dispatch, response.username);
                dispatch({
                    type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_FAILURE,
                    payload: { errorResolution: errorResolution },
                });
            } // show success notification for username update & failed text for session revocation
        }

        function request(): UpdateUsernameAction {
            return { type: UpdateUsernameActionType.UPDATE_USERNAME_REQUEST, payload: {} };
        }

        function success(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            username: string,
        ) {
            dispatch({
                payload: { username },
                type: UpdateUsernameActionType.UPDATE_USERNAME_SUCCESS,
            });
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage(
                        'notifications.account.username.update.success',
                    ),
                ),
            );
            dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
        }

        function successWithSessionRevoke(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            username: string,
            tokenExpirationUrl: string,
        ) {
            dispatch({
                payload: { username },
                type: UpdateUsernameActionType.UPDATE_USERNAME_SUCCESS,
            });
            dispatch({
                type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_SUCCESS,
            });
            dispatch(
                NotificationsActions.showRedirectSuccess(
                    'notification.revoke.session.success.username.update.success',
                ),
            );
            // redirect the user to login page after session revocation
            setInterval(() => {
                ErrorRedirectService.takeToLogout(tokenExpirationUrl.toString());
            }, NOTIFICATIONS[NotificationType.Success_Redirecting].expiry);
            dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
        }

        function failure(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            errorResolution: ErrorResolution,
        ) {
            dispatch({
                payload: { errorResolution },
                type: UpdateUsernameActionType.UPDATE_USERNAME_FAILURE,
            });
            dispatch(
                ErrorResolutionActions.processErrorResolution(
                    errorResolution,
                    'notifications.account.username.update.failure',
                    { challengerPid: data.challengeData.cpid },
                ),
            );
        }
    }
}
